import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LensWorkflowDemo from '../../components/shared/LensWorkflowDemo';
import LuminaOnboardingLightbox, { LIGHTBOX_DISMISSED_KEY as LUMINA_LB_KEY } from '../../components/lumina/LuminaOnboardingLightbox';
import { cn } from '../../lib/utils';
import {
  Mic, MessageSquare, ScanLine, FileText, Clock, CheckCircle2,
  ArrowRight, Users, CalendarDays, Link2, Coins, Sparkles, Zap,
  TrendingUp, Timer, Brain, Copy, Stethoscope, Power, ChevronDown, ChevronUp,
  Circle, Send, Upload, X, ClipboardList, BookOpen
} from 'lucide-react';
import ActivityFeed from '../../components/shared/ActivityFeed';
import { toast } from 'sonner';
import { PatientCardList } from './LuminaMADashboard';
import { useAppSync } from '../../contexts/AppSyncContext';
import AIMorningSummary from '../../components/shared/AIMorningSummary';
import WeeklySummaryCard from '../../components/shared/WeeklySummaryCard';
import FirstRunBanner from '../../components/shared/FirstRunBanner';
import EmptyStateGuide from '../../components/shared/EmptyStateGuide';
import { useIntegrationMode } from '../../contexts/IntegrationModeContext';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_TODAYS_APPOINTMENTS = [
  { id: 'APT-001', patient: 'John Smith', patientId: 'P001', time: '9:00 AM', type: 'Follow-up', chief: 'Diabetes management', status: 'in_progress' },
  { id: 'APT-002', patient: 'Maria Garcia', patientId: 'P002', time: '9:30 AM', type: 'New Patient', chief: 'Annual physical', status: 'not_started' },
  { id: 'APT-003', patient: 'Robert Johnson', patientId: 'P003', time: '10:00 AM', type: 'Sick Visit', chief: 'Persistent cough, low-grade fever x 5 days', status: 'not_started' },
  { id: 'APT-004', patient: 'Emily Davis', patientId: 'P004', time: '10:30 AM', type: 'Follow-up', chief: 'HTN follow-up, medication adjustment', status: 'not_started' },
  { id: 'APT-005', patient: 'James Taylor', patientId: 'P005', time: '11:00 AM', type: 'Follow-up', chief: 'COPD exacerbation follow-up', status: 'not_started' },
  { id: 'APT-006', patient: 'Sarah Williams', patientId: 'P006', time: '11:30 AM', type: 'Acute', chief: 'Acute lower back pain radiating to left leg', status: 'not_started' },
  { id: 'APT-007', patient: 'Michael Brown', patientId: 'P007', time: '1:00 PM', type: 'Follow-up', chief: 'Depression medication check', status: 'not_started' },
  { id: 'APT-008', patient: 'Jennifer Martinez', patientId: 'P008', time: '1:30 PM', type: 'New Patient', chief: 'Migraine evaluation', status: 'not_started' },
];

const DEMO_RECENT_NOTES = [
  { id: 'N-001', patient: 'Sarah Williams', patientId: 'P006', date: '2026-03-23', status: 'draft', chief: 'Lower back pain eval' },
  { id: 'N-002', patient: 'Michael Brown', patientId: 'P007', date: '2026-03-23', status: 'draft', chief: 'Depression follow-up' },
  { id: 'N-003', patient: 'Lisa Anderson', patientId: 'P009', date: '2026-03-22', status: 'signed', chief: 'Annual wellness visit' },
  { id: 'N-004', patient: 'David Kim', patientId: 'P010', date: '2026-03-22', status: 'signed', chief: 'Asthma management' },
];

const DAILY_SUMMARY = {
  timeSaved: '2.5 hrs',
  notesCompleted: 12,
  creditsUsed: 53,
  codingAccuracy: 98.5,
  patientsSeenToday: 8,
  avgNoteTime: '1.8 min',
};

const STATUS_CONFIG = {
  signed: { label: 'Signed', color: 'bg-emerald-50 text-emerald-700' },
  in_progress: { label: 'In progress', color: 'bg-blue-50 text-blue-700' },
  not_started: { label: 'Not started', color: 'bg-slate-100 text-slate-500' },
  draft: { label: 'Draft', color: 'bg-amber-50 text-amber-700' },
  review: { label: 'Ready', color: 'bg-violet-50 text-violet-700' },
};

const LuminaDashboard = () => {
  const { isDemo: isDemoMode } = useAppMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Legacy URL-param demo flag still honored; authoritative appmode check
  // (`isDemoMode`) overrides via `isDemo = isDemoMode || urlFlag`.
  const isDemo = isDemoMode || (searchParams.get('demo') === 'true');
  const appSync = useAppSync();
  const integration = useIntegrationMode();

  // Onboarding lightbox — auto-show on first visit
  const [showLightbox, setShowLightbox] = useState(() => {
    if (isDemo) return true;
    try { return localStorage.getItem(LUMINA_LB_KEY) !== 'true'; } catch { return true; }
  });

  const navigateWithTransition = (path) => {
    const overlay = document.createElement('div');
    overlay.id = 'nav-transition-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#000;z-index:9999;opacity:0;transition:opacity 350ms ease';
    document.body.appendChild(overlay);
    requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    setTimeout(() => { navigate(path); }, 380);
  };

  const handleStartVisit = (apt) => {
    setStartingVisit(apt.id);
    setTimeout(() => {
      navigateWithTransition(`/lumina/clinical-focus?patientId=${apt.patientId}&autoStart=ambient`);
    }, 800);
  };
  const [showLensDemo, setShowLensDemo] = useState(false);
  const [soloMode, setSoloMode] = useState(false);
  const [startingVisit, setStartingVisit] = useState(null);
  const [soloPatients, setSoloPatients] = useState(() =>
    DEMO_TODAYS_APPOINTMENTS.map(apt => ({
      id: apt.id, name: apt.patient, initials: apt.patient.split(' ').map(n => n[0]).join(''),
      time: apt.time, type: apt.type, chief: apt.chief,
      tasks: [
        { key: 'intake', label: 'Intake forms', done: apt.status === 'in_progress', type: 'lens' },
        { key: 'vitals', label: 'Vitals', done: apt.status === 'in_progress', type: 'lens' },
        { key: 'prior_note', label: 'Prior visit note', done: false, type: 'lens' },
        { key: 'insurance', label: 'Insurance card', done: false, type: 'lens' },
      ],
      roomReady: false, docs: [], flagged: false, flagReason: '',
    }))
  );
  const [soloExpanded, setSoloExpanded] = useState(null);
  const [soloCardTab, setSoloCardTab] = useState({});
  const unsignedCount = DEMO_RECENT_NOTES.filter(n => n.status === 'draft').length;

  return (
    <div className="space-y-6" data-testid="lumina-dashboard">

      {/* ── Hero ── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight text-slate-900">
            {isDemo ? 'Good morning, Dr. Chen' : 'Good morning'}
          </h1>
          <p className="text-slate-500 mt-1">{DEMO_TODAYS_APPOINTMENTS.length} appointments today &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/lumina/credits">
            <Card className="border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" data-testid="credit-overview-card">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center">
                  <Coins className="h-4 w-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Balance</p>
                  <p className="text-base font-bold">247 Credits</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Button onClick={() => navigateWithTransition('/lumina/clinical-focus')} className="bg-black hover:bg-gray-800 h-auto py-3 px-5 gap-2" data-testid="start-visits-btn">
            <Zap className="h-4 w-4" /> Start Today's Visits
          </Button>
        </div>
      </div>

      {/* ── AI Morning Summary (Principle 1) — demo-only narrative; real mode
           renders a neutral prompt until the API is wired. ── */}
      <AIMorningSummary
        app="lumina"
        text={isDemo
          ? `Good morning, Dr. Chen. You have ${DEMO_TODAYS_APPOINTMENTS.length} patients today. Based on your schedule, ${DEMO_TODAYS_APPOINTMENTS[0].patient} (${DEMO_TODAYS_APPOINTMENTS[0].time}) has a ${DEMO_TODAYS_APPOINTMENTS[0].chief.toLowerCase()} follow-up \u2014 HbA1c was above target at last visit. ${DEMO_TODAYS_APPOINTMENTS[2].patient} (${DEMO_TODAYS_APPOINTMENTS[2].time}) is a new sick visit with no prior chart data available \u2014 Lens upload recommended.`
          : 'Morning summary will appear once your schedule is connected.'}
      />

      {/* ── Weekly Summary (Principle 8 \u2014 Mondays) ── */}
      {integration.shouldShowWeeklySummary() && (
        <WeeklySummaryCard app="lumina" onDismiss={integration.dismissWeeklySummary} />
      )}

      {/* ── First-Run Banner (Principle 5) ── */}
      {integration.isFirstRun('lumina') && (
        <FirstRunBanner
          app="lumina_provider"
          onLoadSchedule={() => { setShowLensDemo(true); integration.dismissFirstRun('lumina'); }}
          onExploreDemo={() => integration.dismissFirstRun('lumina')}
        />
      )}

      {/* ══════════ ENTER CLINICAL FOCUS ══════════ */}
      <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: '#fafafa', border: '0.5px solid #e8e8e8' }} data-testid="clinical-focus-entry">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-black flex items-center justify-center shrink-0">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 500, color: '#111' }}>Clinical Focus</p>
            <p style={{ fontSize: 12, color: '#888' }}>AI-powered visit documentation — ambient, dictation, and Lens</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLightbox(true)}
            className="flex items-center gap-1.5 transition-colors hover:bg-slate-100"
            style={{ height: 36, padding: '0 14px', borderRadius: 8, background: '#fff', color: '#555', fontSize: 12, fontWeight: 500, border: '0.5px solid #e0e0e0', cursor: 'pointer' }}
            data-testid="getting-started-btn"
          >
            <BookOpen style={{ width: 13, height: 13 }} />
            Getting started
          </button>
          <Button onClick={() => navigateWithTransition('/lumina/clinical-focus')} className="bg-black hover:bg-gray-800 gap-2 shrink-0 h-9 px-5" data-testid="enter-clinical-focus-btn">
            <Power className="h-4 w-4" /> Enter Clinical Focus <ArrowRight className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </div>
      </div>

      {/* ══════════ TODAY'S PATIENT QUEUE ══════════ */}
      <div data-testid="patient-queue-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-900">Today's Patients</h2>
            <Badge variant="outline" className="text-[10px] font-mono">{DEMO_TODAYS_APPOINTMENTS.length} total</Badge>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSoloMode(!soloMode)}
              className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all border",
                soloMode ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-300")}
              data-testid="solo-doctor-toggle">
              <div className={cn("h-3.5 w-7 rounded-full relative transition-colors", soloMode ? "bg-white/30" : "bg-slate-200")}>
                <div className={cn("absolute top-0.5 h-2.5 w-2.5 rounded-full transition-all",
                  soloMode ? "left-3.5 bg-white" : "left-0.5 bg-slate-400")} />
              </div>
              Solo Doctor Mode
            </button>
          </div>
        </div>

        {soloMode && (
          <div className="mb-3 px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2" data-testid="solo-mode-notice">
            <ClipboardList className="h-3.5 w-3.5 text-slate-500" />
            <p className="text-[11px] text-slate-600"><span className="font-semibold">Solo Doctor Mode:</span> Full MA prep workflow active. Scan docs with Lens, complete prep, then start visits.</p>
          </div>
        )}

        {soloMode ? (
          /* Full MA-style patient cards in Solo Doctor Mode */
          <PatientCardList
            patients={soloPatients}
            setPatients={setSoloPatients}
            expandedCard={soloExpanded}
            setExpandedCard={setSoloExpanded}
            cardTab={soloCardTab}
            setCardTab={setSoloCardTab}
            syncActions={{ roomPatient: appSync.roomPatient, sendToBilling: appSync.sendToBilling }}
          />
        ) : (
          /* Standard Provider appointment queue */
          <Card className="border-slate-200">
            <CardContent className="p-0 divide-y divide-slate-100">
              {DEMO_TODAYS_APPOINTMENTS.map((apt) => {
                const sc = STATUS_CONFIG[apt.status] || STATUS_CONFIG.not_started;
                const isStarting = startingVisit === apt.id;
                return (
                  <div key={apt.id} data-testid={`queue-${apt.id}`}>
                    <div
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors group"
                      onClick={() => navigateWithTransition(`/lumina/clinical-focus?patientId=${apt.patientId}`)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-xs font-bold text-slate-500">
                          {apt.patient.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-slate-800">{apt.patient}</p>
                            <span className="text-[10px] text-slate-400 font-mono">{apt.time}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{apt.chief}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={cn("text-[10px] border-0", sc.color)}>{sc.label}</Badge>
                        {isStarting ? (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white">
                            <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-[10px] font-medium">Starting...</span>
                          </div>
                        ) : (
                          <Button size="sm" className="h-7 text-[10px] bg-slate-900 hover:bg-slate-800 gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={e => { e.stopPropagation(); handleStartVisit(apt); }}
                            data-testid={`start-visit-${apt.id}`}>
                            <Zap className="h-2.5 w-2.5" /> Start Visit
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ══════════ RECENT SOAP NOTES ══════════ */}
      <div data-testid="recent-notes-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">Recent SOAP Notes</h2>
            {unsignedCount > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]" data-testid="unsigned-count">{unsignedCount} unsigned</Badge>
            )}
          </div>
          <Link to="/lumina/documentation"><Button variant="ghost" size="sm" className="gap-1 text-xs">View All <ArrowRight className="h-3 w-3" /></Button></Link>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0 divide-y divide-slate-100">
            {DEMO_RECENT_NOTES.map((note) => {
              const sc = STATUS_CONFIG[note.status] || STATUS_CONFIG.draft;
              return (
                <div
                  key={note.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors group"
                  onClick={() => navigate('/lumina/documentation')}
                  data-testid={`note-row-${note.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{note.patient}</p>
                      <p className="text-xs text-slate-400">{note.chief} &middot; {note.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {note.status === 'draft' && (
                      <div className="flex items-center gap-1.5 mr-2">
                        <Sparkles className="h-2.5 w-2.5 text-violet-500" />
                        <span className="text-[10px] text-violet-600 font-medium">AI has suggestions</span>
                      </div>
                    )}
                    {note.status === 'signed' && (
                      <div className="flex items-center gap-1.5 mr-2">
                        <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" />
                        <span className="text-[10px] text-emerald-600 font-medium">Sent to RevQ</span>
                      </div>
                    )}
                    {note.status === 'draft' && (
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-slate-800" onClick={e => { e.stopPropagation(); }} data-testid={`sign-${note.id}`}>
                        <Send className="h-2.5 w-2.5" /> Sign
                      </Button>
                    )}
                    <Badge className={cn("text-[10px] border-0", sc.color)}>{sc.label}</Badge>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* ── EHR Integration CTA ── */}
      <Card className="border-slate-200 bg-slate-50/50" data-testid="ehr-integration-cta">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Link2 className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900">Connect your EHR for seamless sync</p>
              <p className="text-xs text-muted-foreground">Auto-sync appointments, charts, and push notes directly.</p>
            </div>
          </div>
          <Button onClick={() => navigate('/lumina/integrations')} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 gap-2 shrink-0 text-sm">
            <Sparkles className="h-3.5 w-3.5 text-violet-600" /> Setup Integration
          </Button>
        </CardContent>
      </Card>

      {/* ── Daily Summary ── */}
      <div data-testid="daily-summary-report">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900">Daily Summary</h2>
          <Badge variant="outline" className="text-xs gap-1"><Clock className="h-3 w-3" /> Today</Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { icon: Timer, value: DAILY_SUMMARY.timeSaved, label: 'Time Saved', color: 'text-violet-500' },
            { icon: FileText, value: DAILY_SUMMARY.notesCompleted, label: 'Notes Completed', color: 'text-slate-500' },
            { icon: Users, value: DAILY_SUMMARY.patientsSeenToday, label: 'Patients Seen', color: 'text-blue-500' },
            { icon: Zap, value: DAILY_SUMMARY.avgNoteTime, label: 'Avg Note Time', color: 'text-amber-500' },
            { icon: Coins, value: DAILY_SUMMARY.creditsUsed, label: 'Credits Used', color: 'text-amber-500' },
            { icon: TrendingUp, value: `${DAILY_SUMMARY.codingAccuracy}%`, label: 'Coding Accuracy', color: 'text-violet-500' },
          ].map(s => (
            <Card key={s.label}><CardContent className="p-4 text-center">
              <s.icon className={cn("h-5 w-5 mx-auto mb-2", s.color)} />
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
            </CardContent></Card>
          ))}
        </div>
      </div>

      <ActivityFeed app="lumina" maxItems={5} />

      {/* Lens Demo Lightbox (for first-run banner) */}
      <LensWorkflowDemo open={showLensDemo} onOpenChange={setShowLensDemo} />

      {/* Onboarding Lightbox */}
      <LuminaOnboardingLightbox open={showLightbox} onClose={() => setShowLightbox(false)} hasFhir={integration.isFHIR} />
    </div>
  );
};

export default LuminaDashboard;
